export const DEFAULT_GALLERY_ARTWORK_LOAD_QUOTA_MB = 200;

export type GalleryArtworkLoadQuotaStats = {
  enabled: boolean;
  quotaMb: number;
  quotaBytes?: number;
  currentBytes?: number;
  remainingBytes?: number;
  currentMb: number;
  remainingMb: number;
  unknownArtworkIds: string[];
};

export type GalleryStats = {
  availableSlotsCount: number;
  totalListedCount: number;
  openHexagonsCount: number;
  artworkLoadQuota: GalleryArtworkLoadQuotaStats;
};

const normalizeNumber = (value: unknown, fallback: number): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
};

export const normalizeGalleryQuotaStats = (
  quota: Partial<GalleryArtworkLoadQuotaStats> | null | undefined,
  fallbackQuotaMb: number = DEFAULT_GALLERY_ARTWORK_LOAD_QUOTA_MB
): GalleryArtworkLoadQuotaStats => {
  const quotaMb = normalizeNumber(quota?.quotaMb, fallbackQuotaMb);
  const currentMb = normalizeNumber(quota?.currentMb, 0);
  const remainingMb = normalizeNumber(quota?.remainingMb, Math.max(quotaMb - currentMb, 0));

  return {
    enabled: quota?.enabled !== false,
    quotaMb,
    quotaBytes: quota?.quotaBytes,
    currentBytes: quota?.currentBytes,
    remainingBytes: quota?.remainingBytes,
    currentMb,
    remainingMb,
    unknownArtworkIds: Array.isArray(quota?.unknownArtworkIds) ? quota.unknownArtworkIds : [],
  };
};

export const isGalleryArtworkLoadQuotaEnabled = (
  gallery: { spaceType?: string } | null | undefined,
  space: { type?: string; subtype?: string } | null | undefined
): boolean => {
  if (gallery?.spaceType === "mom" || gallery?.spaceType === "sellermom") {
    return false;
  }

  if (space?.type === "mom" || space?.type === "sellermom") {
    return false;
  }

  if (space?.type === "custom" && space?.subtype === "pseudoMoM") {
    return false;
  }

  return true;
};

export const normalizeGalleryStats = (
  stats: Partial<GalleryStats> | null | undefined,
  fallbackQuotaMb: number = DEFAULT_GALLERY_ARTWORK_LOAD_QUOTA_MB
): GalleryStats => ({
  availableSlotsCount: normalizeNumber(stats?.availableSlotsCount, 0),
  totalListedCount: normalizeNumber(stats?.totalListedCount, 0),
  openHexagonsCount: normalizeNumber(stats?.openHexagonsCount, 0),
  artworkLoadQuota: normalizeGalleryQuotaStats(stats?.artworkLoadQuota, fallbackQuotaMb),
});

export const formatQuotaMb = (value: number): string => {
  if (!Number.isFinite(value)) {
    return "0";
  }

  if (value >= 10 || Number.isInteger(value)) {
    return value.toFixed(0);
  }

  return value.toFixed(1);
};

export const getGalleryQuotaUsagePercent = (quota: GalleryArtworkLoadQuotaStats): number => {
  if (!quota.quotaMb) {
    return 0;
  }

  return Math.max(0, Math.min(100, (quota.currentMb / quota.quotaMb) * 100));
};

export const buildSkippedGalleryQuotaMessage = (skippedArtworks: unknown): string | null => {
  if (!Array.isArray(skippedArtworks)) {
    return null;
  }

  const quotaSkips = skippedArtworks.filter((item: any) => {
    const reason = String(item?.reason || "").toLowerCase();
    return Boolean(item?.quota) || reason.includes("quota");
  });
  if (quotaSkips.length === 0) {
    return null;
  }

  const firstReason = String(quotaSkips[0]?.reason || "Gallery display quota prevented listing.");
  return quotaSkips.length === 1
    ? firstReason
    : `${quotaSkips.length} artworks were not listed because of gallery display quota. First reason: ${firstReason}`;
};

export const buildSkippedArtworkMessage = (skippedArtworks: unknown): string | null => {
  if (!Array.isArray(skippedArtworks) || skippedArtworks.length === 0) {
    return null;
  }

  const firstReason = String(skippedArtworks[0]?.reason || "No reason provided.");
  return skippedArtworks.length === 1
    ? `1 artwork was not listed. Reason: ${firstReason}`
    : `${skippedArtworks.length} artworks were not listed. First reason: ${firstReason}`;
};
